import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Search from "@insite/mobius/Icons/Search";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SearchIcon from "@tCentro/Icons/SearchIcon";
import { COLORS, NISCO, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    loaded: !!getProductListDataView(state).value,
    productFilters: state.pages.productList.productFilters,
    hideSearchWithin: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
    siteName: state.context.website.name,
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface NISProductListSearchWithinStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
}

export const searchWithinStyles: NISProductListSearchWithinStyles = {
    titleText: {
        variant: "h6",
    },
    searchTextField: {
        iconProps: { src: SearchIcon },
        cssOverrides: {
            formField: css`
                width: 100%;

                margin-top: 0px;
                div {
                    input {
                        border-radius: 16px !important;
                        padding: 10px 24px !important;
                        padding-right: 24px !important;
                        height: 40px;
                        background: ${COLORS.background.base} !important;
                        color: #747b83 !important;
                        font-size: ${NISCO.TYPOGRAPHY.size.smaller} !important;
                        font-weight: ${TYPOGRAPHY.weight.base};
                        line-height: 14px;
                        border: 1px solid ${COLORS.border.darkShade} !important;
                        font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
                    }
                    span {
                        top: 10px !important;
                        right: 20px !important;
                        width: 18px !important;
                        height: 18px !important;
                        padding: 0px !important;
                        svg {
                            width: 18px;
                            height: 18px;
                        }
                    }
                }
            `,
        },
    },
};

const styles = searchWithinStyles;

const NISProductListSearchWithin: FC<Props> = ({
    loaded,
    addProductFilters,
    productFilters,
    hideSearchWithin,
    siteName,
}) => {
    if (!loaded) {
        return null;
    }

    const [searchText, setSearchText] = useState("");
    const doSearch = () => {
        if (searchText.trim() !== "") {
            const uniqueSearchWithinQueries = searchText
                .split(" ")
                .filter((value, index, array) => array.indexOf(value) === index);

            addProductFilters({ searchWithinQueries: uniqueSearchWithinQueries });
        }
        setSearchText("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    let pageType: string;
    if (productFilters.pageProductLineId) {
        pageType = "Product Line";
    } else if (productFilters.pageBrandId) {
        pageType = "Brand";
    } else if (productFilters.pageCategoryId) {
        pageType = "Category";
    } else {
        pageType = "Results";
    }

    const label = translate(`Search Within ${pageType}`);

    if (hideSearchWithin) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {/* <Typography forwardAs="label" htmlFor="productsKeywordSearch" {...styles.titleText}>
                {label}
            </Typography> */}
            <TextField
                {...styles.searchTextField}
                iconClickableProps={{ onClick: doSearch, "aria-label": `Search within ${pageType}` }}
                value={searchText}
                onChange={event => setSearchText(event.currentTarget.value)}
                onKeyDown={handleKeyDown}
                placeholder={translate("Search results")}
                type="text"
                data-test-selector="NISProductListSearchWithin"
                id="productsKeywordSearch"
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NISProductListSearchWithin),
    definition: {
        group: "Product List",
        displayName: "NIS Search Within",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
